<template>
    <wrapper-features-templates-operations
    :scrollValue="scrollValue"
	:showLoader="showLoader"
	:showOverlayLoader="showOverlayLoader"
	:applyErrorState="applyErrorState && !showLoader"
	:hideContentWrapper="showAccountsEmptyState"
    :showStepper="activeStep > 0 && activeStep <= stepperParams.length && showContent"
	:activeStep="activeStep"
	:stepperParams="stepperParams"
    :showFooter="activeStep > 0 && activeStep < 3 && showContent"
	:footerButtonsParams="footerButtonsParams"
	:feedbackOperationData="feedbackOperationData"
	:tokenTitleAppend="tokenTitle"
    :tokenCustomUseParams="{
        resendTokenAction: 'transfersKwik/resendOTPForAccountAliasAssociation',
        resendPayload: formData.originAccount,
        forceResendChannelString: formData.authMethodType === 'PN' ? 'sms' : 'email'
    }"
    @challengeCancelHandler="challengeCancelHandler"
    @challengeSuccessHandler="challengeSuccessHandler"
    @challengeErrorHandler="errorHandler($event, addAccountAlias)"
	@clearScrollToValue="scrollValue = null"
    @backOrCloseClicked="emit('backOrCloseClicked')">
        <!-- *********************************************
		*                      Content                   *
		********************************************** -->
		<component
		v-if="showContent"
        :is="getActiveStepComponent"
		:formData="formData"
		:getAccountsList="getAccountsBalances"
        :customerInfo="{ ...getAllCustomerInfo, smsPhoneNumber: getAccountAliasSMSPhoneNumber }"
		:mandatoryList="mandatoryList"
		:showMandatoryErrors="showMandatoryErrors"
		:responseData="responseData"
        :searchInfoForSelectedAccount="searchInfoForSelectedAccount"
		@scrollTo="scrollValue = $event"
        @updateFormData="updateFormDataHandler"
        @showOverlayLoader="showOverlayLoader = true"
        @hideOverlayLoader="showOverlayLoader = false"
        @formErrorsCleared="clearFormErrors = false"
		@resetShowMandatoryErrorsProp="showMandatoryErrors = false"
        @goToStep1="[goToStep(1), formData.alias = getAccountAliasSMSPhoneNumber]"
        @tokenTitle="tokenTitle = $event"/>
        <!-- Empty-state -->
		<template #empty-state-slot>
			<transition name="common-enter-animation" mode="out-in" appear>
				<empty-state-generic
				v-if="showAccountsEmptyState"
				illustration="emptyState/batchfile-cards"
				:title="t('transfers.kwik.accountManagement.emptyState.title')"
				:description="t('transfers.kwik.accountManagement.emptyState.description')"/>
			</transition>
		</template>
    </wrapper-features-templates-operations>
    <!-- Feedback Toasts -->
	<toast-generic
	v-if="showFeedbackToast"
	:toastType="feedbackToastData.toastType"
	:feedbackText="feedbackToastData.text"
	:buttonsParams="feedbackToastData.buttons"
	@hideMe="showFeedbackToast = false"/>
</template>

<script setup>
import WrapperFeaturesTemplatesOperations from '@/components/wrappers/featuresTemplates/Operations'
import EmptyStateGeneric from '@/components/generic/emptyStates/Generic'
import ToastGeneric from '@/components/generic/toasts/Generic'

/* Vue */
import { ref, watch, computed, defineAsyncComponent, onBeforeUnmount } from 'vue'

/* Aux */
import { errorObjectConstructor, emptyObjectConstructor, sucessObjectConstrutor, warningObjectConstrutor } from '@/assets/scripts/toastsHelpers'
import { clearObjectStrings } from '@/assets/scripts/objects'

/* Store */
import { mapActions, mapGetters } from '@/store/helpers/mappers'
import { moduleMapper } from '@/store/helpers/modules.map'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

/* Emmitters */
const emit = defineEmits(['backOrCloseClicked'])

/* mapActions & mapGetters */
const { requestAccountsBalances, requestAccountAssociatedAlias, clearFeatureStates, addAccountAliasAssociation } = mapActions(moduleMapper.TRANSFERS_KWIK)
const { getAccountsBalances, getAccountAliasSMSPhoneNumber } = mapGetters(moduleMapper.TRANSFERS_KWIK)
const { getAllCustomerInfo } = mapGetters(moduleMapper.SESSION)

/* Show Data */
const showLoader = ref(false)
const showOverlayLoader = ref(false)
const showAccountsEmptyState = computed(() => {	return !showLoader.value && !applyErrorState.value && !getAccountsBalances.value.length })
const showContent = computed(() => { return !showLoader.value && !applyErrorState.value && !!getAccountsBalances.value.length })

/* Data */
const scrollValue = ref(null)
const applyErrorState = ref(false)
const showMandatoryErrors = ref(false)
const clearFormErrors = ref(false)
const tokenTitle = ref('')
const searchInfoForSelectedAccount = ref(false)

/*************************************************
*                   Steps Logic                  *
*************************************************/
const activeStep = ref(0)

const stepperParams = ref([
	{ description: t('transfers.kwik.accountManagement.stepper.data') },
	{ description: t('transfers.kwik.accountManagement.stepper.validation') }
])

const getActiveStepComponent = computed(() => {
	const component = activeStep.value === 0 ? 'AccountManagement' : `Step${activeStep.value}`

	return defineAsyncComponent(() => import('@/components/features/transfers/kwik/accountManagement/' + component))
})

const goToStep = stepNumber => {
	activeStep.value = stepNumber
	scrollValue.value = 0
}

/*************************************************
*                Form Logic Data                 *
*************************************************/
const formData = ref({
	/* Account Data */
	originAccount: '',
	accountDisplayInfo: '',
	currency: '',
    aliasType: 'PN',
    authMethodType: 'PN',
    alias: ''
})

const mandatoryList = ref([
	'alias'
])

const updateFormDataHandler = ({ fieldName, inputValue }) => {
	formData.value[fieldName] = inputValue
}

/*************************************************
*             Submit Transfer Logic              *
*************************************************/
const addAccountAlias = async () => {
	showOverlayLoader.value = true

	const payload = {
        accountId: formData.value.originAccount,
        params: {
            aliasType: formData.value.aliasType,
            authMethodType: formData.value.authMethodType
        }
	}

	try {
		await addAccountAliasAssociation(payload)
		showOverlayLoader.value = false
        tokenTitle.value = t('transfers.kwik.accountManagement.registTokenTitle')
	} catch (error) {
		if (error.toString() === 'Cancel') return
		errorHandler(error.response.data, addAccountAlias)
	}
}

/*************************************************
*             Success/Error Handlers             *
*************************************************/
const feedbackOperationData = ref(null)
const responseData = ref()

const showFeedbackToast = ref(false)
const feedbackToastData = ref(emptyObjectConstructor())

const challengeCancelHandler = () => {
    if (activeStep.value === 0) return

    activeStep.value = 0

    feedbackToastData.value = warningObjectConstrutor(t('transfers.kwik.accountManagement.aliasPendingWarningMessage'))
    showFeedbackToast.value = true
    searchInfoForSelectedAccount.value = true
    initialRequests()
}

const challengeSuccessHandler = async data => {
    if (activeStep.value === 0) {
        feedbackToastData.value = sucessObjectConstrutor(data.message || t('transfers.kwik.accountManagement.genericSuccessMessage'))
        showFeedbackToast.value = true
        showOverlayLoader.value = true

        try {
            await requestAccountAssociatedAlias({ accountId: formData.value.originAccount, params: { showPending: true } })

            showOverlayLoader.value = false
        } catch (error) {
            if (error.toString() === 'Cancel') return
            errorHandler(error.response.data)
        }
    } else {
        responseData.value = {
            ...data.data,
            errorLevel: data.errorLevel,
            code: data.code
        }

        feedbackOperationData.value = {
            status: data.errorLevel,
            title: data.message,
            additionalMessage: data.additionalMessage || ''
        }

        goToStep(3)
    }
}

const errorHandler = (data, retryFn) => {
	showLoader.value = false
	showOverlayLoader.value = false

	feedbackToastData.value = errorObjectConstructor(data, retryFn, () => { goToStep(0) })
	showFeedbackToast.value = true
}

/*************************************************
*               Footer/Buttons Logic             *
*************************************************/
/* Footer Buttons Data */
const footerButtonsData = computed(() => {
	return [{
		label: t('transfers.kwik.accountManagement.btn.clear'),
		buttonPosition: 'left',
		hidden: activeStep.value !== 1,
		callbackFunction: () => { clearFormData() },
		step: 1
	}, {
		label: t('transfers.kwik.accountManagement.btn.forward'),
		icon: 'arrow-right-full',
		buttonType: 'primary',
		iconPosition: 'right',
		hidden: activeStep.value !== 1,
		callbackFunction: () => { validateMandatoryFormValues() }
	}, {
		label: t('transfers.kwik.accountManagement.btn.edit'),
		icon: 'arrow-left-full',
		hidden: activeStep.value !== 2,
		callbackFunction: () => { goToStep(1) }
	}, {
		label: t('transfers.kwik.accountManagement.btn.validate'),
		icon: 'check-line',
		buttonType: 'primary',
		iconPosition: 'right',
		hidden: activeStep.value !== 2,
		callbackFunction: () => { addAccountAlias() }
	}]
})

const footerButtonsParams = computed(() => {
	return footerButtonsData.value.filter(ele => !ele.hidden)
})

const clearFormData = () => {
	clearFormErrors.value = true
	clearObjectStrings(formData.value)

	/* Reset form */
	formData.value = {
		...formData.value,
		originAccount: getAccountsBalances.value.find(account => account.default).id,
		accountDisplayInfo: getAccountsBalances.value.find(account => account.default).accountNumber,
		currency: getAccountsBalances.value.find(account => account.default).currency,
        aliasType: 'PN',
        authMethodType: 'PN',
        alias: getAccountAliasSMSPhoneNumber.value
	}

	feedbackOperationData.value = null
	responseData.value = null
	showOverlayLoader.value = false
}

const validateMandatoryFormValues = () => {
    if (formData.value.aliasType === 'NN') {
        showMandatoryErrors.value = true
        return
    }

    if ((formData.value.aliasType === 'EM' && !getAllCustomerInfo.value.customer.email) ||
        (formData.value.aliasType === 'PN' && !getAccountAliasSMSPhoneNumber.value)) {
        feedbackToastData.value = errorObjectConstructor({
            message: t('transfers.kwik.accountManagement.noEmailErrorMessage'),
            dismissToastBtnText: t('commonButtons.gotIt')
        })

        showFeedbackToast.value = true
        return
    }

    goToStep(2)
}

/*************************************************
*                    Handlers                    *
*************************************************/

/*************************************************
*                Initial Requets                 *
*************************************************/
const initialRequests = async () => {
	applyErrorState.value = false
	showLoader.value = true

	try {
		await requestAccountsBalances()
		if (getAccountsBalances.value.length && !searchInfoForSelectedAccount.value) {
			/* Setting Initial Selected Account */
			formData.value.originAccount = getAccountsBalances.value.find(account => account.default).id
			/* Setting Initial Account Display Info from Default Account */
			formData.value.accountDisplayInfo = getAccountsBalances.value.find(account => account.default).accountNumber
			/* Setting Initial Currency from Default Account */
			formData.value.currency = getAccountsBalances.value.find(account => account.default).currency
		}
		showLoader.value = false
	} catch (error) {
		applyErrorState.value = true
		if (error.toString() === 'Cancel') return
		errorHandler(error.response?.data, initialRequests)
	}
}

watch(() => activeStep.value, (newVal, oldVal) => {
	scrollValue.value = 0
})

initialRequests()

onBeforeUnmount(() => {
	clearFeatureStates()
})
</script>

<style lang="scss" scoped>
</style>
